/**
 * Vehicle DTO + related lookups exposed by rental-service `/api/vehicles`.
 * Mirrors `net.nawam.rental.model.entity.Vehicle` filtered by `VRead` /
 * `VReadDetail` JsonViews.
 */

export type VehicleStatus =
  | 'AVAILABLE'
  | 'UNAVAILABLE'
  | 'RENTED'
  | 'UNDER_MAINTENANCE'
  | 'UNDER_REVIEW'
  | 'PRE_UNDER_REVIEW'
  | 'DELETED';

export type FuelType = 'PETROL' | 'DIESEL' | 'ELECTRIC';
export type GearBox = 'MANUAL' | 'AUTOMATIC';
export type DocusignStatus = 'awaiting' | 'completed' | 'declined';
export type ImageType =
  | 'GENERAL'
  | 'REGISTRATION_CARD'
  | 'INSURANCE'
  | 'TECHNICAL_INSPECTION';

/** Simple named-entity shape used by Make / Model / Category / Type. */
export interface NamedRef {
  id: number;
  name?: string | null;
  entityCode?: string | null;
}

export interface Image {
  id?: number;
  url?: string | null;
  type?: ImageType | null;
}

import type { UserRef } from './user.types';
import type { VehicleOption } from './option.types';

export interface VehicleOwnerRef {
  id: number;
  businessName?: string | null;
  ownerType?: 'INDIVIDUAL' | 'COMPANY' | null;
  userId?: number | null;
  /** Read-model du user lié (firstName, lastName, email…). */
  user?: UserRef | null;
}

export interface Vehicle {
  // ── BaseEntity (note : the boolean BaseEntity.status is shadowed by the
  //   workflow `status: VehicleStatus` below — only the enum is exposed
  //   via the JsonView). ───────────────────────────────────────────────
  id: number;
  createdAt?: string | null;
  updatedAt?: string | null;

  // ── ownership ─────────────────────────────────────────────────────────
  /** Deprecated. Use `vehicleOwner.userId` instead. */
  userId?: number | null;
  vehicleOwner?: VehicleOwnerRef | null;

  // ── identity ──────────────────────────────────────────────────────────
  make: NamedRef | null;
  model: NamedRef | null;
  year: number;
  category: NamedRef | null;
  type: NamedRef | null;
  licencePlate: string | null;

  // ── provision window ──────────────────────────────────────────────────
  provisionStartDate?: string | null;
  provisionEndDate?: string | null;

  // ── technical ─────────────────────────────────────────────────────────
  fuelType: FuelType | null;
  gearbox: GearBox | null;
  cylinderCapacity?: number;
  numberOfDoors?: number;
  fuelConsumptionRate?: number;
  securityRate?: number;
  comfortRate?: number;

  // ── location / media ──────────────────────────────────────────────────
  location: string | null;
  imgCover?: string | null;

  // ── business ──────────────────────────────────────────────────────────
  /** Workflow status. Conflicts naming-wise with BaseEntity `status: boolean`
   *  but is the actual operational status of the vehicle. */
  status: VehicleStatus;
  dailyRate: number;
  qrcode?: string | null;
  /** Épinglé « à la une » par un admin — passe en tête de `GET /vehicles/featured`.
   *  Toggle admin : `PATCH /vehicles/{id}/featured?value=` (§2.3a). */
  featured?: boolean;

  // ── documents obligatoires — expiration (#55) ─────────────────────────
  /** Expiration de l'assurance (`yyyy-MM-dd'T'HH:mm:ss`). */
  insuranceExpiryDate?: string | null;
  /** Expiration de la visite technique. */
  technicalInspectionExpiryDate?: string | null;
  /** Dérivé (lecture seule) : au moins un document expiré → véhicule masqué de la
   *  recherche + non réservable jusqu'au renouvellement par l'owner. */
  docsExpired?: boolean;

  // ── docusign / contract ───────────────────────────────────────────────
  docusignSubmissionStatus?: DocusignStatus | null;
  docusignSubmissionId?: number | null;
  docusignSubmissionLink?: string | null;
  /** Detail-only — PDF link. */
  provisionContract?: string | null;

  // ── review trail ──────────────────────────────────────────────────────
  reviewedAt?: string | null;
  reviewedBy?: number | null;
  rejectionReason?: string | null;

  // ── stats / collections (detail) ──────────────────────────────────────
  avgRating?: number;
  photos?: Image[];
  technicalInspection?: Image[];
  registrationCard?: Image[];
  insurance?: Image[];
  /** Équipements rattachés (read-only côté admin — c'est le owner qui gère). */
  options?: VehicleOption[];
}

import { defensiveStatusMeta } from '../helpers/status-meta';

/** UI helper : maps a status to its label + Tailwind palette suffix.
 *  Wrapped in `defensiveStatusMeta` so unknown / null statuses get a
 *  `{label:'—', palette:'gray'}` fallback instead of crashing the template.
 */
export const VEHICLE_STATUS_META = defensiveStatusMeta<VehicleStatus>({
  AVAILABLE: { label: 'Disponible', palette: 'brand' },
  RENTED: { label: 'En location', palette: 'blue-light' },
  UNDER_REVIEW: { label: 'En revue', palette: 'warning' },
  PRE_UNDER_REVIEW: { label: 'Brouillon', palette: 'gray' },
  UNDER_MAINTENANCE: { label: 'En maintenance', palette: 'warning' },
  UNAVAILABLE: { label: 'Indisponible', palette: 'gray' },
  DELETED: { label: 'Supprimé', palette: 'error' },
});

export const FUEL_TYPE_LABEL: Record<FuelType, string> = {
  PETROL: 'Essence',
  DIESEL: 'Diesel',
  ELECTRIC: 'Électrique',
};

export const GEARBOX_LABEL: Record<GearBox, string> = {
  MANUAL: 'Manuelle',
  AUTOMATIC: 'Automatique',
};
